import { useParams } from 'react-router-dom';
import { Box, Button, MenuItem,Typography, useTheme } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { MarkupFieldOptionHotels, MarkupRuleOperator } from '@bandido/shared/types';

import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledSwitch } from '@frontend/features/common/components/ControlledSwitch';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { LoadingButton } from '@mui/lab';
import { TFunction } from 'i18next';
import { useGetMarkupRulesById } from '@frontend/features/markup-rules/queries';

// Campos de condición de Rutero como literales LOCALES (patrón
// TravelIntelligenceSettings): un valor runtime nuevo de @bandido/shared puede
// llegar undefined hasta que Vite re-optimice el dist cacheado de shared.
// El backend los valida igual contra MarkupFieldOptionRutero.
const MARKUP_FIELD_OPTION_RUTERO = {
  PROVIDER: 'PROVIDER',
  VERTICAL: 'VERTICAL',
  DESTINATION: 'DESTINATION',
  CURRENCY: 'CURRENCY',
  PAX_QTY: 'PAX_QTY',
} as const;

type MarkupFieldRutero =
  (typeof MARKUP_FIELD_OPTION_RUTERO)[keyof typeof MARKUP_FIELD_OPTION_RUTERO];

type MarkupField = MarkupFieldOptionHotels | MarkupFieldRutero;

// serviceType de la regla: 'hotel' usa los campos de hotel; los verticales de
// Rutero (docs/RUTERO.md) usan los campos genéricos de MarkupFieldOptionRutero.
export const MARKUP_SERVICE_TYPES = [
  'hotel',
  'rail',
  'ferry',
  'tours',
  'circuits',
  'transfers',
  'cars',
  'esims',
] as const;

interface MarkupRulesFormValues {
  name: string;
  serviceType: string;
  /** Posición deseada (1 = primera; inserta y corre las demás). Vacío = al final. */
  order?: number;
  isActive: boolean;
  conditions: {
    field: NonNullable<MarkupField | undefined>;
    operator: NonNullable<MarkupRuleOperator | undefined>;
    value: string
  }[];
  markupType: boolean;
  markupValue: number;
}

export const markupRulesSchema = (t: TFunction<"translation", undefined>) => Yup.object({
  name: Yup.string()
    .required(t('markupRules.yup.nameRequired'))
    .max(50, t('markupRules.yup.nameCharLength')),
  isActive: Yup.boolean().required(t('markupRules.yup.operatorRequired')),
  serviceType: Yup.string()
    .required(t('markupRules.yup.fieldRequired'))
    .oneOf(MARKUP_SERVICE_TYPES as unknown as string[], t('markupRules.yup.fieldInvalid')),
  order: Yup.number()
    .transform((value, original) =>
      original === '' || original == null || Number.isNaN(value) ? undefined : value,
    )
    .integer()
    .min(1, t('markupRules.yup.valueNotNegative'))
    .optional(),
  conditions: Yup.array()
    .of(
      Yup.object({
        field: Yup.mixed<MarkupField>()
          .required(t('markupRules.yup.fieldRequired'))
          .oneOf(
            [
              ...Object.values(MarkupFieldOptionHotels),
              ...Object.values(MARKUP_FIELD_OPTION_RUTERO),
            ],
            t('markupRules.yup.fieldInvalid')
          ),
        operator: Yup.mixed<MarkupRuleOperator>()
          .required(t('markupRules.yup.operatorRequired'))
          .oneOf(
            Object.values(MarkupRuleOperator),
            t('markupRules.yup.operatorInvalid')
          ),
        value: Yup.string()
          .required(t('markupRules.yup.valueRequired'))
          .max(100, t('markupRules.yup.valueCharLength')),
      })
    )
    // hotel exige al menos una condición (comportamiento histórico); los
    // verticales de Rutero permiten regla sin condiciones (catch-all).
    .when('serviceType', {
      is: 'hotel',
      then: (schema) => schema.min(1, t('markupRules.yup.minConditions')),
    })
    .required(),
  markupType: Yup.boolean().required(t('markupRules.yup.valueRequired')),
  markupValue: Yup.number()
    .required(t('markupRules.yup.markupRequired'))
    .min(0, t('markupRules.yup.valueNotNegative'))
    .max(100, t('markupRules.yup.valueCharLength')),
});

export const MarkupRulesEdit = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { ruleId, id: agencyId } = useParams();
  const theme = useTheme();
  const { addToast } = useSnackBar();

  const methods = useForm<MarkupRulesFormValues>({
    resolver: yupResolver(markupRulesSchema(t)),
    defaultValues: {
      name: '',
      serviceType: 'hotel',
      order: undefined,
      isActive: true,
      conditions: [],
      markupType: true,
      markupValue: 10,
    },
  });

  const { data: dbRule } = useGetMarkupRulesById({
    id: ruleId
  })
  const { handleSubmit, control } = methods;

  const onSubmit = async (data: MarkupRulesFormValues) => {

    const request = {
        name: data.name,
        serviceType: data.serviceType,
        type: data.markupType ? 'percentage' : 'fixed',
        value: data.markupValue,
        isActive: data.isActive,
        agencyId: Number(agencyId),
        ...(data.order ? { order: data.order } : {}),
        conditions: data.conditions
    }

    setLoading(true);
        try {
          const response =
            ruleId === 'create'
              ? await client.post<{ order?: number }>('/markup-rules', request)
              : await client.patch<{ order?: number }>(`/markup-rules/${ruleId}`, request);

          addToast({
            message: t('markupRules.createSuccess'),
            color: 'success',
          });

          // El orden pedido puede recortarse al último lugar disponible.
          if (data.order && response.data?.order && response.data.order !== data.order) {
            addToast({
              message: t('markupRules.orderAssigned', { order: response.data.order }),
              color: 'info',
            });
          }
        } catch (e) {
          addToast({
            message: t('markupRules.createError'),
            color: 'error',
          });
    
        } finally {
          setLoading(false)
          // TODO : navigate al edit
        }

  };
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditions',
  });


  const serviceType = methods.watch('serviceType');
  const isHotelRule = serviceType === 'hotel';

  const handleAddConditionBlock = () =>
    append({
      field: isHotelRule
        ? MarkupFieldOptionHotels.HOTEL_VENDOR
        : MARKUP_FIELD_OPTION_RUTERO.PROVIDER,
      operator: MarkupRuleOperator.EQUALS,
      value: '',
    });

  const markupFieldOptions = isHotelRule
    ? Object.entries(MarkupFieldOptionHotels).map(([key, value]) => ({
        value,
        label: t(`markupRules.hotelMarkupFieldOptions.${key}`),
      }))
    : Object.entries(MARKUP_FIELD_OPTION_RUTERO).map(([key, value]) => ({
        value,
        label: t(`markupRules.ruteroMarkupFieldOptions.${key}`),
      }));

  const serviceTypeOptions = MARKUP_SERVICE_TYPES.map((value) => ({
    value,
    label: t(`markupRules.serviceTypes.${value}`),
  }));

  const operatorFieldOptions = Object.entries(MarkupRuleOperator).map(([key, value]) => ({
    value,
    label: t(`markupRules.operators.${key}`)
  }));

  useEffect(() => {
    if (ruleId && ruleId !== 'create' && dbRule) {
      methods.reset({
        name: dbRule.name,
        // el byId aliasea "servicetype" AS "serviceType"; el tipo del query
        // del front todavía declara la variante en minúsculas
        serviceType:
          (dbRule as unknown as { serviceType?: string }).serviceType ??
          dbRule.servicetype ??
          'hotel',
        order: dbRule.order,
        isActive: dbRule.isactive,
        conditions: dbRule.conditions.map((condition) => ({
          field: condition.field as NonNullable<MarkupField>,
          operator: condition.operator as NonNullable<MarkupRuleOperator>,
          value: condition.value,
        })),
        markupType: dbRule.type === 'percentage',
        markupValue: dbRule.value,
      });
    }
  }, [ruleId, dbRule, methods]);
  

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ListLayout
          padding={0}
          title={ruleId === 'create' ? t('markupRules.viewEdit.titleNew') : t('markupRules.viewEdit.titleEdit')}
          subtitle={ruleId === 'create' ? t('markupRules.viewEdit.subtitleNew') : t('markupRules.viewEdit.subtitleEdit')}
        >
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Typography variant="h6">{t('markupRules.name')}</Typography>
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <Typography variant="body2">{t('markupRules.isActive')}</Typography>
                <ControlledSwitch
                  control={control}
                  name="isActive"
                />
              </Box>
            </Box>
            <ControlledTextField
              control={control}
              name="name"
              placeholder={t('markupRules.name')}
              sx={{ height: theme.spacing(5), width: '566px' }}
            />
            <Typography variant="h6" mt={2}>{t('markupRules.serviceType')}</Typography>
            <ControlledSelect
              control={control}
              name="serviceType"
              size="small"
              sx={{ width: '566px' }}
              required
            >
              {serviceTypeOptions.map((optionItem) => (
                <MenuItem key={optionItem.value} value={optionItem.value}>
                  {optionItem.label}
                </MenuItem>
              ))}
            </ControlledSelect>
            <Typography variant="h6" mt={2}>{t('markupRules.order')}</Typography>
            <ControlledTextField
              control={control}
              name="order"
              type="number"
              placeholder={t('markupRules.orderPlaceholder')}
              helperText={t('markupRules.orderHelper')}
              sx={{ width: '566px' }}
            />
          </Box>

          <Box
            display="flex"
            padding={theme.spacing(4)}
            flexDirection="column"
            alignItems="flex-start"
            borderRadius={"8px"}
            border={`1px solid ${theme.palette.divider}`}
            margin={theme.spacing(3, 0)}
            gap={3}
          >

            <Box width="100%">
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="h6">Conditions</Typography>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="baseline" gap={2}>
                {fields.map((_f, index) => (
                  <Box key={index} display="flex" justifyContent="flex-start" width="100%">
                    <Box display="flex" alignItems="center" width="100%" gap={2}>
                      <ControlledSelect fullWidth size="small" control={control} name={`conditions.${index}.field`} required>
                        {markupFieldOptions.map((optionItem, index) => (
                          <MenuItem key={index} value={optionItem.value}>
                            {optionItem.label}
                          </MenuItem>
                        ))}
                      </ControlledSelect>
                      <ControlledSelect control={control} name={`conditions.${index}.operator`} size="small" fullWidth required>
                        {operatorFieldOptions.map((optionItem, index) => (
                          <MenuItem key={index} value={optionItem.value}>
                            {optionItem.label}
                          </MenuItem>
                        ))}
                      </ControlledSelect>
                      <ControlledTextField name={`conditions.${index}.value`} size="small" control={control} fullWidth />
                      <Button onClick={() => remove(index)} variant="text" style={{ padding: theme.spacing(0) }} startIcon={<Delete color="error" />} />
                    </Box>
                  </Box>
                ))}
              </Box>


            </Box>


            <Button size="large" sx={{ color: theme.palette.primary.main }} onClick={handleAddConditionBlock}>
              {t('markupRules.addCondition')}
            </Button>
          </Box>

          <Box
            display="flex"
            padding={theme.spacing(4)}
            flexDirection="column"
            alignItems="flex-start"
            borderRadius={"8px"}
            border={`1px solid ${theme.palette.divider}`}
            margin={theme.spacing(3, 0)}
            gap={3}
          >

            <Box width="100%">
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="h6">Markup</Typography>

                <Box display="flex" gap={1}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2">{t('markupRules.fee')}</Typography>
                    <ControlledSwitch control={control} name="markupType" color='default' defaultChecked />
                    <Typography variant="body2">{t('markupRules.percentage')}</Typography>
                  </Box>
                  <ControlledTextField
                    control={control}
                    name={`markupValue`}
                    type="number"
                    inputProps={{
                      min: 0,
                      style: { textAlign: 'right' }
                    }}
                    sx={{
                      width: 80,
                    }}
                  />
                </Box>

              </Box>
            </Box>

          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" color="error" sx={{ marginRight: theme.spacing(1) }}>
              {t('markupRules.buttonDiscard')}
            </Button>
            <LoadingButton loading={loading} type="submit" variant="contained" color="primary">
              {t('common.save')}
            </LoadingButton>
          </Box>
        </ListLayout>
      </form>
    </FormProvider>
  );
};
